/**
 * Readiness hooks for the publisher's ad globals. GPT and Prebid both expose a
 * command queue (`googletag.cmd` / `pbjs.que`) that runs callbacks once the
 * library loads — pushing into the stub array is the standard "wait for it"
 * mechanism, no polling needed, and works whether the library loads before or
 * after the SDK.
 */

import type { Googletag, PrebidJs } from './types';

type AdWindow = Window & {
  googletag?: Partial<Googletag>;
  _pbjsGlobals?: string[];
  [key: string]: unknown;
};

const win = (): AdWindow => window as unknown as AdWindow;

/** Runs `cb` with the live googletag once GPT is ready (immediately if it already is). */
export function whenGptReady(cb: (gt: Googletag) => void): void {
  const w = win();
  w.googletag = w.googletag ?? {};
  const gt = w.googletag;
  gt.cmd = gt.cmd ?? [];
  gt.cmd.push(() => cb(w.googletag as Googletag));
}

/**
 * Discovers the publisher's Prebid global (`_pbjsGlobals` when a wrapper renames
 * it, `pbjs` otherwise) and runs `cb` once Prebid processes its queue.
 */
export function whenPrebidReady(cb: (pbjs: PrebidJs) => void): void {
  const w = win();
  let key = 'pbjs';
  try {
    const globals = w._pbjsGlobals;
    if (globals && globals.length > 0) {
      for (const candidate of globals) {
        if (typeof w[candidate] !== 'undefined') {
          key = candidate;
          break;
        }
      }
    }
  } catch {
    /* keep default */
  }
  const pbjs = (w[key] = (w[key] as PrebidJs | undefined) ?? ({} as PrebidJs));
  pbjs.que = pbjs.que ?? [];
  pbjs.que.push(() => cb(pbjs));
}
